export const METHOD = {
  UTM: 'UTM',
  IMM: 'IMM',
  FIXED_AMOUNT: 'FIXED_AMOUNT',
}

/** External official links (Poder Judicial, etc.). */
export const EXTERNAL_LINKS = {
  TRAMITE_FACIL_UTM: 'https://ojv.pjud.cl/kpitec-ojv-web/tramite_facil',
}

/**
 * DB storage values (canonical). Matches METHOD for UTM/IMM.
 */
export const DB_METHOD = {
  UTM: 'UTM',
  IMM: 'IMM',
  FIXED_AMOUNT: 'FIXED_AMOUNT',
}

/** @deprecated Pre-rename rows; normalizePensionMethod still accepts these. */
export const LEGACY_METHOD = {
  PERCENTAGE: 'PERCENTAGE',
  IMM_PERCENTAGE: 'IMM_PERCENTAGE',
}

export const METHOD_DETAILS = [
  {
    value: METHOD.UTM,
    label: 'UTM',
    description: 'Se reajusta todos los meses según el valor vigente de la UTM.',
    primary: true,
  },
  {
    value: METHOD.IMM,
    label: '% del sueldo mínimo',
    description:
      'Se reajusta cuando sube el sueldo mínimo, no mes a mes.',
    primary: false,
    legacy: true,
  },
  {
    value: METHOD.FIXED_AMOUNT,
    label: 'Monto fijo en pesos',
    description: 'Formato antiguo en CLP. Se recomienda convertir a UTM.',
    primary: false,
    legacy: true,
  },
]

/** Selectable methods in onboarding / preferences (excludes gated FIXED_AMOUNT). */
export const CONFIGURABLE_METHODS = [METHOD.UTM, METHOD.IMM]

/** Ops ficha: admin may set any stored method, including legacy fixed amount. */
export const ADMIN_CONFIGURABLE_METHODS = [
  METHOD.UTM,
  METHOD.IMM,
  METHOD.FIXED_AMOUNT,
]

/**
 * UTM quantity bounds matching Postgres NUMERIC(8, 5):
 * up to 3 integer digits + 5 decimal digits (0.00001 … 999.99999).
 */
export const UTM_QUANTITY = {
  MAX: 999.99999,
  DECIMAL_PLACES: 5,
  STEP: '0.00001',
}

/**
 * Accepts Chilean comma decimals ("9,8") for Postgres numeric ("9.8").
 */
export const normalizeDecimalInput = (raw) => {
  if (raw == null || raw === '') return raw
  return String(raw).trim().replace(',', '.')
}

/**
 * Validates a UTM quantity against NUMERIC(8, 5) rules.
 * @returns {{ valid: boolean, error: string }}
 */
export const validateUtmQuantity = (raw) => {
  if (raw === '' || raw == null) {
    return { valid: false, error: 'Ingresa la cantidad de UTM.' }
  }

  const normalized = normalizeDecimalInput(raw)
  const parsed = Number(normalized)

  if (!Number.isFinite(parsed) || Number.isNaN(parsed)) {
    return { valid: false, error: 'Ingresa la cantidad de UTM.' }
  }

  if (parsed <= 0) {
    return { valid: false, error: 'La cantidad de UTM debe ser mayor a 0.' }
  }

  const decimalPart = normalized.includes('.')
    ? normalized.split('.')[1] || ''
    : ''
  if (decimalPart.length > UTM_QUANTITY.DECIMAL_PLACES) {
    return {
      valid: false,
      error: 'Usa como máximo 5 decimales (formato del tribunal).',
    }
  }

  if (parsed > UTM_QUANTITY.MAX) {
    return {
      valid: false,
      error: 'La cantidad de UTM no puede ser mayor a 999,99999.',
    }
  }

  return { valid: true, error: '' }
}

export const isValidUtmQuantity = (raw) => validateUtmQuantity(raw).valid

export const normalizePensionMethod = (method) => {
  if (method === LEGACY_METHOD.PERCENTAGE) return METHOD.UTM
  if (method === METHOD.UTM) return METHOD.UTM
  if (method === METHOD.IMM || method === LEGACY_METHOD.IMM_PERCENTAGE) {
    return METHOD.IMM
  }
  if (method === METHOD.FIXED_AMOUNT) return METHOD.FIXED_AMOUNT
  return METHOD.UTM
}

/**
 * UTM quantity for the form.
 * Stored in pension_configs.percentage; fetch maps that into `amount` for the UI.
 */
export const getUtmQuantityFromConfig = (config) => {
  if (!config) return ''
  if (normalizePensionMethod(config.method) !== METHOD.UTM) return ''
  return config.amount ?? config.percentage ?? ''
}

export const getImmPercentageFromConfig = (config) => {
  if (!config) return ''
  if (normalizePensionMethod(config.method) !== METHOD.IMM) return ''
  return config.percentage ?? ''
}

export const getFixedAmountFromConfig = (config) => {
  if (!config) return ''
  if (normalizePensionMethod(config.method) !== METHOD.FIXED_AMOUNT) return ''
  return config.amount ?? ''
}

export const getPaymentDayFromConfig = (config) => {
  if (!config) return null
  const raw =
    config.payment_day ?? config.paymentDay ?? null
  if (raw == null || raw === '') return null
  const day = Number(raw)
  if (!Number.isInteger(day) || day < 1 || day > 31) return null
  return day
}

/**
 * Stable key for method + factor so we can tell if a save should recalc.
 * @param {object|null} config
 * @returns {string}
 */
export const getPensionConfigInputsKey = (config) => {
  if (!config) return 'none'
  const method = normalizePensionMethod(config.method)
  if (method === METHOD.UTM) {
    return `UTM:${normalizeDecimalInput(getUtmQuantityFromConfig(config))}`
  }
  if (method === METHOD.IMM) {
    return `IMM:${normalizeDecimalInput(getImmPercentageFromConfig(config))}`
  }
  return `FIXED:${normalizeDecimalInput(getFixedAmountFromConfig(config))}`
}

/**
 * Effective calendar day for a given month when payment_day is 1–31.
 * If the month has fewer days (e.g. 31 in February), clamps to the last day.
 * @param {number} year Full year (e.g. 2026)
 * @param {number} month 1–12
 * @param {number} paymentDay Configured day 1–31
 * @returns {number|null}
 */
export const getEffectivePaymentDay = (year, month, paymentDay) => {
  const day = Number(paymentDay)
  const y = Number(year)
  const m = Number(month)
  if (
    !Number.isInteger(day) ||
    day < 1 ||
    day > 31 ||
    !Number.isInteger(y) ||
    !Number.isInteger(m) ||
    m < 1 ||
    m > 12
  ) {
    return null
  }
  // Day 0 of next month = last day of `month`
  const daysInMonth = new Date(y, m, 0).getDate()
  return Math.min(day, daysInMonth)
}
